"use client";

import {
	IconChevronUp,
	IconDeviceMobile,
	IconLogout,
	IconMoon,
	IconSun,
} from "@tabler/icons-react";
import { Link, useRouter } from "@tanstack/react-router";
import type React from "react";
import { useContext, useState } from "react";
import { toast } from "sonner";
import {
	ThemeContext,
	type ThemeContextInterface,
} from "@/components/providers/theme-provider";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebarSafe } from "@/components/ui/sidebar";
import { UserNavigation } from "@/data/navigation/user";
import { hasMobileAppStoreLinks, isMobileAppEnabled } from "@/lib/mobile-app";
import { cn } from "@/lib/utils";
import { apiClient } from "@/utils/api";
import { suppressNextUnauthorizedRedirectToast } from "@/utils/auth-session";
import {
	deleteAccessToken,
	deleteRefreshToken,
	getRefreshToken,
} from "@/utils/local-storage";
import { useAuth } from "../providers/auth";

export function NavUser({
	user,
	menuPosition,
}: {
	user: {
		firstName: string;
		lastName: string;
		email: string;
		emoji: string;
	};
	menuPosition?: "right" | "left" | "bottom" | "top";
}) {
	const router = useRouter();
	const { refresh } = useAuth();
	const { state, isMobile } = useSidebarSafe();

	const { darkTheme, toggleTheme } = useContext(
		ThemeContext,
	) as ThemeContextInterface;

	const Logout = async () => {
		// Revoke the current device session before dropping tokens (best effort —
		// local logout must succeed even when the API is unreachable).
		const refreshToken = getRefreshToken();
		if (refreshToken) {
			try {
				await apiClient.api.authLogoutCreate({ token: refreshToken });
			} catch {
				// Best effort — proceed with local sign-out regardless.
			}
		}
		suppressNextUnauthorizedRedirectToast();
		deleteAccessToken();
		deleteRefreshToken();
		localStorage.removeItem("teamID");
		refresh();
		toast.success("Goodbye! 👋");
		router.navigate({ to: "/auth/login" });
	};

	const isCollapsed = state === "collapsed" || isMobile;
	const [menuOpen, setMenuOpen] = useState(false);

	return (
		<DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
			<DropdownMenuTrigger
				className={`focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground h-12 gap-2 px-2.5 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground ${isCollapsed ? "size-8 p-0 justify-center" : ""}`}
			>
				<div className="size-8 bg-background rounded-md flex items-center justify-center text-xl shrink-0">
					{user.emoji}
				</div>
				{!isCollapsed && (
					<>
						<div className="grid flex-1 text-left text-sm leading-tight min-w-0">
							{user.firstName && user.lastName ? (
								<>
									<span className="truncate font-semibold">
										{user.firstName} {user.lastName}
									</span>
									<span className="truncate text-xs">{user.email}</span>
								</>
							) : (
								<span className="truncate font-semibold">{user.email}</span>
							)}
						</div>
						<IconChevronUp
							className={cn(
								"ml-auto group-data-[collapsible=icon]:hidden transition-transform duration-200 ease-in-out",
								menuOpen && "rotate-180",
							)}
						/>
					</>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
				side={menuPosition}
				align="end"
				sideOffset={4}
			>
				<div className="text-muted-foreground px-1.5 py-1.5 text-sm font-normal">
					<div className="flex items-center gap-2 text-left">
						<div className="size-8 bg-muted rounded-lg flex items-center justify-center text-2xl">
							{user.emoji}
						</div>
						<div className="grid flex-1 text-left text-sm leading-tight">
							{user.firstName && user.lastName ? (
								<>
									<span className="truncate font-semibold text-foreground">
										{user.firstName} {user.lastName}
									</span>
									<span className="truncate text-xs">{user.email}</span>
								</>
							) : (
								<span className="truncate font-semibold text-foreground">
									{user.email}
								</span>
							)}
						</div>
					</div>
				</div>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
						<div className="flex items-center gap-2">
							{darkTheme ? (
								<IconMoon className="size-4 shrink-0" />
							) : (
								<IconSun className="size-4 shrink-0" />
							)}
							<span>Toggle to {darkTheme ? "light" : "dark"} theme</span>
						</div>
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				{isMobileAppEnabled() && hasMobileAppStoreLinks() ? (
					<>
						<DropdownMenuGroup>
							<DropdownMenuItem
								{...({ asChild: true } as unknown as React.ComponentProps<
									typeof DropdownMenuItem
								>)}
								className="cursor-pointer"
							>
								<Link
									to="/download"
									className="flex items-center gap-2 w-full h-full"
								>
									<IconDeviceMobile className="size-4 shrink-0" />
									<span>Get mobile app</span>
								</Link>
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
					</>
				) : null}
				<DropdownMenuGroup>
					{UserNavigation.map((item) => (
						<DropdownMenuItem
							{...({ asChild: true } as unknown as React.ComponentProps<
								typeof DropdownMenuItem
							>)}
							key={item.title}
							className="cursor-pointer"
						>
							<Link
								to={item.url}
								className="flex items-center gap-2 w-full h-full"
							>
								<item.icon className="size-4 shrink-0" />
								<span>{item.title}</span>
							</Link>
						</DropdownMenuItem>
					))}
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={Logout} className="cursor-pointer">
					<div className="flex items-center gap-2">
						<IconLogout className="size-4 shrink-0" />
						<span>Log out</span>
					</div>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
